import { Download, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { AppUser, PatientStatus, RiskLevel, fetchDiseaseGroups, fetchStaff } from '@/api/adapters/patients';
import { toast } from '@/app/store/toast.store';
import { useAuthCanViewAll } from '@/app/store/auth.store';
import { EmptyTableRow } from '@/components/feedback/EmptyTableRow';
import { TableSkeleton } from '@/components/feedback/TableSkeleton';
import { Button } from '@/components/ui/Button';
import { RiskPill } from '@/components/ui/RiskPill';
import { Select } from '@/components/ui/Select';
import { useModalEffects } from '@/shared/hooks/use-modal-effects';
import { usePatientsList } from '@/shared/hooks/use-patients-list';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useExportPatientsCsv } from '@/shared/hooks/use-export-patients-csv';
import { formatDate, maskBirth, maskMrn, maskNameKeepLast, maskPhone, patientStatusLabelKey } from '../utils';

interface Props {
	t: (key: string, opts?: Record<string, unknown>) => string;
	onClose: () => void;
	navigate: (path: string) => void;
}

export function PatientListModal({ t, onClose, navigate }: Props) {
	const panelRef = useRef<HTMLDivElement>(null);

	useModalEffects({ open: true, onClose, modalRef: panelRef });

	// Primary-diagnosis filter options come from the admin disease-group master (FEATURE_014),
	// keyed by the real group id, not a hardcoded 5-domain list.
	const { data: diseaseGroups = [] } = useQuery({
		queryKey: ['disease', 'groups'],
		queryFn: fetchDiseaseGroups,
	});

	// 담당의 filter + column are visible only to canViewAll roles (NURSE·CRC) — PRD 02 §3.5.8.
	const canViewAll = useAuthCanViewAll();
	const { data: staffOptions = [] } = useQuery({
		queryKey: ['staff', 'all'],
		queryFn: () => fetchStaff(),
		enabled: canViewAll,
	});

	const {
		patients,
		total,
		isLoading,
		isError,
		filters,
		setFilter,
		setSort,
		resetFilters,
		refetch,
	} = usePatientsList();

	const [searchInput, setSearchInput] = useState('');
	// PRD 02 §3.5.2 — 200ms search debounce (modal-specific; the default hook delay is 300ms).
	const debouncedSearch = useDebounce(searchInput, 200);

	useEffect(() => {
		setFilter('search', debouncedSearch);
	}, [debouncedSearch, setFilter]);

	// PRD 02 v1.14 §3.5.3 — [초기화] 는 「전체 필터·검색·정렬」 을 되돌린다. 검색어는 이 컴포넌트의
	// 로컬 state 라 `resetFilters()` 가 닿지 않고, 되돌린 뒤에도 `debouncedSearch` 가 그대로여서
	// 동기화 effect 도 다시 돌지 않는다. 그래서 입력란에는 검색어가 남은 채 목록만 그 조건 없이
	// 조회되어, 화면이 적용 중인 조건을 잘못 알려 주고 있었다.
	const handleReset = useCallback(() => {
		setSearchInput('');
		resetFilters();
	}, [resetFilters]);

	const exportCsv = useExportPatientsCsv();

	const handleExportCsv = () => {
		if (!total) {
			toast.warning(t("medical:home.patientList.csvExportNoData"));
			return;
		}
		// Server-side export (FEATURE_013; patient-management PRD §7): the BE returns the full care-team-scoped,
		// filtered set as a CSV blob and writes the required audit row. Pass the current filters, trigger the
		// browser download. The exported PHI is MASKED server-side per PRD 11 §3.3 ("환자명(마스킹)",
		// "식별 마스킹 적용") — a previous note here claimed the opposite as "a confirmed decision"; it cited no
		// source and SHCT-584 corrected the backend to match the PRD.
		exportCsv.mutate({ filters, total });
	};

	const handleRowClick = (uid: string) => {
		navigate(`/patients?selected=${uid}&tab=pro`);
		onClose();
	};

	// Column count drives skeleton/empty/error colSpan; 담당의 adds one column for canViewAll roles.
	const colCount = canViewAll ? 13 : 12;
	// Distinguish "no match for the current filters" from "nothing to show at all" (PRD 02 §3.5.11).
	const hasActiveFilter =
		!!searchInput ||
		(filters.status && filters.status !== 'ALL') ||
		!!filters.riskLevel ||
		!!filters.primaryDx ||
		!!filters.staff ||
		!!filters.dischargeMonths ||
		!!filters.delayedOnly;

	const renderSortTh = (label: string, key: string) => {
		const isActive = filters.sortKey === key;
		const icon = isActive ? (filters.sortDir === 'asc' ? ' ▲' : ' ▼') : '';
		const cls = `th-sort${isActive ? ' th-sort--active' : ''}`;
		return (
			<th className={cls} onClick={() => setSort(key)}>
				{label}
				<span className='th-sort__icon'>{icon}</span>
			</th>
		);
	};

	return (
		<div
			className='home-modal'
			onClick={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
		>
			<div className='home-modal__overlay' onClick={onClose} />
			<div ref={panelRef} className='home-modal__panel home-modal__panel--list'>
				<div className='home-modal__header'>
					<h3 className='home-modal__title'>
						{t('medical:home.patientList.title')}
					</h3>
					<button
						type='button'
						className='home-modal__close'
						onClick={onClose}
						aria-label={t('common:actions.close')}
					>
						<X size={24} />
					</button>
				</div>
				<div className='home-modal__body flex flex-col overflow-hidden'>
					{/* Filter bar */}
					<div className='patient-list__filter-bar'>
						<div className='pl-filter pl-filter--search flex-1 min-w-52'>
							<input
								type='text'
								className='input'
								placeholder={t('medical:home.patientList.filters.search')}
								value={searchInput}
								onChange={(e) => setSearchInput(e.target.value)}
							/>
						</div>
						<Select
							label={t('medical:home.patientList.filters.status')}
							value={filters.status || 'ALL'}
							onChange={(v) => setFilter('status', v)}
							options={[
								{
									value: 'ALL',
									label: t('medical:home.patientList.filters.all'),
								},
								{
									value: 'active',
									label: t('medical:home.patientList.filters.active'),
								},
								{
									value: 'closed',
									label: t('medical:home.patientList.filters.closed'),
								},
							]}
						/>
						<Select
							label={t('medical:home.patientList.filters.risk')}
							value={filters.riskLevel || ''}
							onChange={(v) => setFilter('riskLevel', v)}
							options={[
								{ value: '', label: t('medical:home.patientList.filters.all') },
								{
									value: RiskLevel.G3,
									label: t('medical:home.patientList.filters.high'),
								},
								{
									value: RiskLevel.G2,
									label: t('medical:home.patientList.filters.moderate'),
								},
								{
									value: RiskLevel.G1,
									label: t('medical:home.patientList.filters.low'),
								},
								{
									value: "UNKNOWN",
									label: t("medical:home.patientList.filters.unknown"),
								},
							]}
						/>
						<Select
							label={t('medical:home.patientList.filters.primaryDx')}
							value={filters.primaryDx || ''}
							onChange={(v) => setFilter('primaryDx', v)}
							options={[
								{ value: '', label: t('medical:home.patientList.filters.all') },
								...diseaseGroups.map((g) => ({
									value: String(g.id),
									label: `${g.name} (${g.range})`,
								})),
							]}
						/>
						{canViewAll && (
							<Select
								label={t('medical:home.patientList.filters.staff')}
								value={filters.staff || ''}
								onChange={(v) => setFilter('staff', v)}
								options={[
									{
										value: '',
										label: t('medical:home.patientList.filters.all'),
									},
									...staffOptions.map((s) => ({
										value: s.email,
										label: s.department ? `${s.name} (${s.department})` : s.name,
									})),
								]}
							/>
						)}
						<Select
							label={t('medical:home.patientList.filters.discharge')}
							value={filters.dischargeMonths || ''}
							onChange={(v) => setFilter('dischargeMonths', v)}
							options={[
								{ value: '', label: t('medical:home.patientList.filters.all') },
								{
									value: '1',
									label: t('medical:home.patientList.filters.month1'),
								},
								{
									value: '3',
									label: t('medical:home.patientList.filters.month3'),
								},
								{
									value: '6',
									label: t('medical:home.patientList.filters.month6'),
								},
								{
									value: '12',
									label: t('medical:home.patientList.filters.month12'),
								},
							]}
						/>
						<div className='pl-filter pl-filter--checks'>
							<label className='pl-check'>
								<input
									type='checkbox'
									checked={filters.delayedOnly || false}
									onChange={(e) => setFilter('delayedOnly', e.target.checked)}
								/>
								{t('medical:home.patientList.filters.delayedOnly')}
							</label>
						</div>
						<Button
							variant='ghost'
							className='btn--outline border-gray-300'
							onClick={handleReset}
						>
							{t('medical:home.patientList.filters.reset')}
						</Button>
						<Button
							variant='ghost'
							className='btn--outline'
							disabled={exportCsv.isPending}
							onClick={handleExportCsv}
						>
							<Download size={16} /> {t('medical:home.patientList.filters.csv')}
						</Button>
					</div>

					{/* Count */}
					<div className='patient-list__count px-4 py-2 text-gray-500'>
						{t('medical:home.patientList.count', {
							filtered: patients.length,
							total,
						})}
					</div>

					{/* Table */}
					<div className='flex-1 overflow-auto min-h-0'>
						<table className='data-table patient-list__table table'>
							<thead>
								<tr>
									{renderSortTh(
										t('medical:home.patientList.columns.uid'),
										'uid',
									)}
									{renderSortTh(
										t('medical:home.patientList.columns.mrn'),
										'mrn',
									)}
									{renderSortTh(
										t('medical:home.patientList.columns.name'),
										'name',
									)}
									{renderSortTh(
										t('medical:home.patientList.columns.gender'),
										'gender',
									)}
									{renderSortTh(
										t('medical:home.patientList.columns.birthDate'),
										'birthDate',
									)}
									<th>{t('medical:home.patientList.columns.phone')}</th>
									{renderSortTh(
										t('medical:home.patientList.columns.dischargeDate'),
										'dischargeDate',
									)}
									{renderSortTh(
										t('medical:home.patientList.columns.diagnosis'),
										'primaryDxName',
									)}
									{renderSortTh(
										t('medical:home.patientList.columns.rValue'),
										'riskScore',
									)}
									{canViewAll && (
										<th>{t('medical:home.patientList.columns.staff')}</th>
									)}
									{renderSortTh(
										t('medical:home.patientList.columns.delayed'),
										'delayed',
									)}
									{renderSortTh(
										t('medical:home.patientList.columns.status'),
										'status',
									)}
									{renderSortTh(
										t('medical:home.patientList.columns.appRegisteredAt'),
										'appRegisteredAt',
									)}
								</tr>
							</thead>
							<tbody>
								{isLoading ? (
									<TableSkeleton rows={8} cols={colCount} />
								) : isError ? (
									<EmptyTableRow
										colSpan={colCount}
										message={t('medical:home.patientList.loadError')}
										action={
											<Button variant='secondary' onClick={() => refetch()}>
												{t('medical:home.error.retry')}
											</Button>
										}
									/>
								) : patients.length === 0 ? (
									<EmptyTableRow
										colSpan={colCount}
										message={t(
											hasActiveFilter
												? 'medical:home.patientList.emptyFiltered'
												: 'medical:home.patientList.emptyNone',
										)}
									/>
								) : (
									patients.map((p) => (
										<tr
											key={p.uid}
											onClick={() => handleRowClick(p.uid)}
											className='cursor-pointer'
										>
											<td>{p.uid}</td>
											<td>{maskMrn(p.mrn)}</td>
											<td className='whitespace-nowrap'>
												<strong>{maskNameKeepLast(p.name)}</strong>
												{p.appUser === AppUser.GUARDIAN && p.guardian && (
													<span
														className='guardian-chip ml-1.5'
														title={t(
															'medical:home.patientList.guardianTooltip',
															{
																relation:
																	t(
																		'medical:patients.register.staff.relationship.' +
																			p.guardian.relationship,
																	) || p.guardian.relationship,
															},
														)}
													>
														{t('medical:home.patientList.guardianChip')}
													</span>
												)}
											</td>
											<td>{p.gender}</td>
											<td>{maskBirth(p.birthDate)}</td>
											<td>{maskPhone(p.phone)}</td>
											<td>{p.dischargeDate}</td>
											<td>
												<span className='badge badge-primary'>
													{p.primaryDxName}
												</span>
												{p.secondaryDx && (
													<div className='text-xs text-gray-500 mt-1'>
														{p.secondaryDx}
														{p.secondaryDxName ? ` · ${p.secondaryDxName}` : ''}
													</div>
												)}
											</td>
											<td className='whitespace-nowrap'>
												{/* SHCT-828 (2차) — 이 표만 옛 표기(회색 `grade-chip--r` + 별도
												    `r-badge`)로 남아 있어 고객이 다시 보고했다(2026-09-04).
												    같은 RiskPill로 바꾸면 세 가지가 함께 맞는다:
												      · 위험도가 알약 색에 반영된다
												      · UNKNOWN + 점수 있음 → 예전에는 '저위험' 배지로 잘못 찍혔다
												        (아래 삼항이 G3/G2가 아니면 전부 low로 떨어졌다)
												      · 점수 null → 회색 '—' 대신 PRD 11 §7.2의 산출 불가 / R — */}
												<RiskPill
													riskScore={p.riskScore}
													riskLevel={p.riskLevel}
														status={p.status}
													t={t}
												/>
											</td>
											{canViewAll && (
												<td className='whitespace-nowrap'>
													{p.primaryStaff || (
														<span className='text-gray-400'>—</span>
													)}
												</td>
											)}
											<td>
												{p.delayed ? (
													<span className='delay-flag delay-flag--yes'>
														{t('medical:home.patientList.delayFlag.yes')}
													</span>
												) : (
													<span className='delay-flag delay-flag--no'>
														{t('medical:home.patientList.delayFlag.no')}
													</span>
												)}
											</td>
											<td>
												{/* SHCT-775 — 의료진웹 PRD 02 §3.5.4 column 12: `status`(관리중/관리종료·사유).
												    This cell used to print the *filter dropdown's* labels (활성/종료) and drop
												    the close reason, so the same patient read 종료 here and 관리종료 · 기타 on
												    the patient card. Both screens now resolve their label through one helper,
												    which is what keeps them from drifting apart again. */}
												<span
													className={`badge ${p.status === PatientStatus.ACTIVE ? 'badge-success' : 'badge-gray'}`}
												>
													{t(
														patientStatusLabelKey(p.status, p.closedReason),
													)}
												</span>
											</td>
											<td className='whitespace-nowrap'>
												{p.appRegisteredAt ? (
													formatDate(p.appRegisteredAt.slice(0, 10))
												) : (
													<span className='text-gray-400'>
														{t('medical:home.patientList.notRegistered')}
													</span>
												)}
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	);
}
